// LoginPage — the auth screen at /login. After sign-in the user is sent
// to the safe `?returnTo=...` (default /app/workout). F-3: a logged-in
// visitor is redirected to returnTo immediately; the form is never
// shown. The screen is also offline-aware: if navigator.onLine is
// false, the form is disabled and the offline message is shown.

import { useState } from "react";
import type { FormEvent, ReactElement } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useAuth } from "../../services/auth";
import { AuthError } from "../../services/authErrors";
import { safeReturnTo } from "../routing/returnTo";

export function LoginPage(): ReactElement {
  const { t } = useI18n();
  const { signIn, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get("returnTo"));
  const online = typeof navigator === "undefined" ? true : navigator.onLine;

  // F-3: a logged-in visit to /login redirects to returnTo. The form
  // never renders for an authenticated user, so a phishing link
  // /login?returnTo=/login cannot show a credential re-entry prompt
  // on the real domain.
  if (user) {
    return <Navigate to={returnTo} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!online) return;
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      if (err instanceof AuthError) {
        setError(t(authKey(err.code)));
      } else {
        setError(t("auth.error.unknown"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="login-page"
      className="mx-auto flex min-h-screen w-full max-w-[420px] flex-col gap-6 px-6 py-12"
    >
      <header>
        <p className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-lime)]">
          {t("brand.name")}
        </p>
        <h1 className="font-display mt-2 text-4xl uppercase leading-[1.1] tracking-[0.02em] text-[var(--color-ink-hi)]">
          {t("auth.login.title")}
        </h1>
      </header>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
        data-testid="login-form"
        aria-disabled={!online}
      >
        <label className="flex flex-col gap-1">
          <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
            {t("auth.field.email")}
          </span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={!online || submitting}
            data-testid="email-input"
            className="border border-[var(--color-line-strong)] bg-[var(--color-slab)] px-3 text-[var(--color-ink-hi)]"
            style={{
              minHeight: "64px",
              borderRadius: "var(--radius-sharp)",
              fontSize: 18,
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
            {t("auth.field.password")}
          </span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            disabled={!online || submitting}
            data-testid="password-input"
            className="border border-[var(--color-line-strong)] bg-[var(--color-slab)] px-3 text-[var(--color-ink-hi)]"
            style={{
              minHeight: "64px",
              borderRadius: "var(--radius-sharp)",
              fontSize: 18,
            }}
          />
        </label>
        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            data-testid="forgot-password-link"
            className="font-sans text-sm text-[var(--color-lime)] underline-offset-2 hover:underline"
          >
            {t("auth.login.forgotPassword")}
          </Link>
        </div>
        {error && (
          <p
            data-testid="login-error"
            role="alert"
            className="font-sans text-sm"
            style={{ color: "var(--color-blood)" }}
          >
            {error}
          </p>
        )}
        {!online && (
          <p
            data-testid="login-offline"
            className="font-sans text-sm"
            style={{ color: "var(--color-ink-mute)" }}
          >
            {t("auth.offline.loginNeedsNetwork")}
          </p>
        )}
        <button
          type="submit"
          disabled={!online || submitting}
          data-testid="login-submit"
          className="font-display uppercase tracking-[0.02em] text-[var(--color-void)]"
          style={{
            minHeight: "64px",
            fontSize: 22,
            backgroundColor: "var(--color-lime)",
            borderRadius: "var(--radius-sharp)",
            opacity: !online || submitting ? 0.6 : 1,
          }}
        >
          {t("auth.login.submit")}
        </button>
      </form>
      <p className="font-sans text-sm text-[var(--color-ink-mute)]">
        {t("auth.login.noAccount")}{" "}
        <Link to="/signup" className="text-[var(--color-lime)] underline-offset-2 hover:underline">
          {t("auth.login.signUpLink")}
        </Link>
      </p>
    </main>
  );
}

function authKey(code: string): string {
  switch (code) {
    case "wrong-password":
      return "auth.error.wrongPassword";
    case "email-taken":
      return "auth.error.emailTaken";
    case "invalid-email":
      return "auth.error.invalidEmail";
    case "weak-password":
      return "auth.error.weakPassword";
    case "offline":
      return "auth.error.offline";
    default:
      return "auth.error.unknown";
  }
}
